// Client-side endpoint-list verification — docs/04-censorship-resistance.md §2.
//
// A list is trusted only if it is well-formed (protocol schema), signed by a
// key the client pins (`signedBy` → keyring), and — when updating — carries a
// STRICTLY greater version than the installed one. Distribution channels are
// untrusted by design; this signature is the entire trust story.
//
// Signing input (fixed by frontend/endpoints, validated against its committed
// fixture vectors): utf8(SIG_DOMAIN.ENDPOINTS + "\n") || utf8(canonicalJSON(doc
// sans `signature`)). Browser-compatible: WebCrypto/@noble via ed25519.ts —
// no Node APIs.

import { EndpointList, SIG_DOMAIN } from '@merkury/protocol';
import { base64ToBytes, concatBytes, utf8 } from '../bytes.js';
import { canonicalize } from './canonicalJson.js';
import { ed25519Verify } from './ed25519.js';

/** Maps a `signedBy` key id to that key's base64 raw Ed25519 public key. */
export type Keyring = Readonly<Record<string, string>>;

export type VerifyFailure = 'malformed' | 'unknown-signer' | 'bad-signature' | 'stale-version';

export type VerifyResult =
  | { ok: true; document: EndpointList }
  | { ok: false; reason: VerifyFailure; detail: string };

export interface VerifyOptions {
  /** Pinned signer keys the client trusts (baked into the app build). */
  keyring: Keyring;
  /**
   * When set, the document is a candidate *update*: rejected as stale unless
   * `version > currentVersion`. Omit for a standalone check.
   */
  currentVersion?: number;
}

/** The exact bytes the list's Ed25519 signature is checked over. */
export function endpointListSigningBytes(doc: Record<string, unknown>): Uint8Array {
  const unsigned: Record<string, unknown> = { ...doc };
  delete unsigned['signature'];
  return concatBytes(utf8(SIG_DOMAIN.ENDPOINTS + '\n'), utf8(canonicalize(unsigned)));
}

/**
 * Verify an untrusted endpoint-list document. Never throws — returns a tagged
 * result so the signaling client can branch on cause (and keep its current
 * list on any failure).
 */
export async function verifyEndpointList(
  input: unknown,
  opts: VerifyOptions,
): Promise<VerifyResult> {
  const parsed = EndpointList.safeParse(input);
  if (!parsed.success) {
    return { ok: false, reason: 'malformed', detail: parsed.error.message };
  }
  const doc = parsed.data;

  const publicKeyB64 = opts.keyring[doc.signedBy];
  if (publicKeyB64 === undefined) {
    return {
      ok: false,
      reason: 'unknown-signer',
      detail: `no pinned key for signedBy="${doc.signedBy}"`,
    };
  }

  const publicKey = base64ToBytes(publicKeyB64);
  const signature = base64ToBytes(doc.signature);
  if (!publicKey || !signature) {
    return { ok: false, reason: 'malformed', detail: 'key or signature is not valid base64' };
  }

  const valid = await ed25519Verify(
    publicKey,
    endpointListSigningBytes(doc as unknown as Record<string, unknown>),
    signature,
  );
  if (!valid) {
    return { ok: false, reason: 'bad-signature', detail: 'Ed25519 signature check failed' };
  }

  if (opts.currentVersion !== undefined && doc.version <= opts.currentVersion) {
    return {
      ok: false,
      reason: 'stale-version',
      detail: `version ${doc.version} is not newer than current ${opts.currentVersion}`,
    };
  }

  return { ok: true, document: doc };
}
